import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ApiError, api } from './api';
import type {
  AccountOverview,
  AnswerResult,
  AutoPlanResult,
  CancelOutcome,
  ChangePlanQuote,
  CommentView,
  DeckSummary,
  Edict,
  FavoriteItem,
  FavoriteListResponse,
  FavoriteSort,
  LedgerEntry,
  NoteItem,
  Pacote,
  Paged,
  PerformanceDashboard,
  PlannerRange,
  ReviewGrade,
  SessionRow,
  SimuladoPool,
  SimuladoResult,
  StatsOverview,
  StudyCard,
  StudySession,
  SubtopicNode,
} from './types';

/**
 * Chaves de cache centralizadas.
 *
 * Ter as chaves espalhadas pelos componentes foi o que tornou impossível saber
 * o que invalidar depois de responder um card. Aqui, `qk.study(...)` é a única
 * definição e a invalidação é sempre exacta.
 */
export const qk = {
  decks: ['decks'] as const,
  pacotes: ['pacotes'] as const,
  deckTree: (deckId: number) => ['deck-tree', deckId] as const,
  study: (params: {
    deckId: number;
    subtopic: string | null;
    modoRevisao: boolean;
    janelaDias: number;
    somenteFavoritas?: boolean;
    pacote?: string | null;
    desde?: string | null;
    ate?: string | null;
    ultimasErradas?: boolean;
  }) =>
    [
      'study',
      params.deckId,
      params.subtopic ?? '',
      params.modoRevisao,
      params.janelaDias,
      params.somenteFavoritas ?? false,
      params.pacote ?? '',
      params.desde ?? '',
      params.ate ?? '',
      params.ultimasErradas ?? false,
    ] as const,
  card: (cardId: number) => ['card', cardId] as const,
  comentarios: (cardId: number) => ['comentarios', cardId] as const,
  favoritas: (filters: FavoriteFilters) => ['favoritas', filters] as const,
  notas: (page: number) => ['notas', page] as const,
  caderno: ['caderno'] as const,
  stats: (desde: string, ate: string, deckId: number | null) =>
    ['stats', desde, ate, deckId ?? 0] as const,
  desempenho: ['desempenho'] as const,
  planner: (de: string, ate: string) => ['planner', de, ate] as const,
  plannerSubtopics: (deckId: number) => ['planner-subtopics', deckId] as const,
  editais: ['editais'] as const,
  conta: ['conta'] as const,
  ledger: ['ledger'] as const,
  sessoes: ['sessoes'] as const,
  tempoHoje: ['tempo-hoje'] as const,
};

/**
 * Uma consulta cujo 404/401 não deve ser repetido. O padrão do React Query é
 * três tentativas, o que atrasa a mensagem de erro em vários segundos.
 */
const semRepetirErroDeCliente = {
  retry: (falhas: number, error: Error) => {
    if (error instanceof ApiError && error.status < 500) return false;
    return falhas < 2;
  },
};

// ── Baralhos ────────────────────────────────────────────────────────────────

export function useDecks(): UseQueryResult<DeckSummary[]> {
  return useQuery({
    queryKey: qk.decks,
    queryFn: async () => (await api<{ decks: DeckSummary[] }>('/decks')).decks,
    staleTime: 60_000,
    ...semRepetirErroDeCliente,
  });
}

export function usePacotes(): UseQueryResult<Pacote[]> {
  return useQuery({
    queryKey: qk.pacotes,
    queryFn: async () => (await api<{ pacotes: Pacote[] }>('/decks/pacotes')).pacotes,
    // O catálogo só muda quando o superadmin importa cards.
    staleTime: 10 * 60_000,
    ...semRepetirErroDeCliente,
  });
}

export function useDeckTree(deckId: number | null): UseQueryResult<SubtopicNode[]> {
  return useQuery({
    queryKey: qk.deckTree(deckId ?? 0),
    enabled: deckId !== null,
    queryFn: async () =>
      (await api<{ tree: SubtopicNode[] }>(`/decks/${deckId}/tree`)).tree,
    staleTime: 10 * 60_000,
    ...semRepetirErroDeCliente,
  });
}

// ── Estudo ──────────────────────────────────────────────────────────────────

export interface StudyParams {
  deckId?: number;
  subtopic: string | null;
  modoRevisao: boolean;
  janelaDias: number;
  somenteFavoritas?: boolean;
  pacote?: string | null;
  desde?: string | null;
  ate?: string | null;
  ultimasErradas?: boolean;
}

export function useStudySession(
  params: StudyParams | null,
): UseQueryResult<StudySession> {
  return useQuery({
    queryKey:
      params === null
        ? ['study', 'idle']
        : qk.study({ ...params, deckId: params.deckId ?? 0 }),
    enabled: params !== null,
    queryFn: () =>
      api<StudySession>('/estudo/session', {
        query: {
          deckId: params!.deckId,
          subtopic: params!.subtopic,
          modoRevisao: params!.modoRevisao ? 1 : 0,
          janelaDias: params!.janelaDias,
          somenteFavoritas: params!.somenteFavoritas ? 1 : undefined,
          pacote: params!.pacote,
          desde: params!.desde,
          ate: params!.ate,
          ultimasErradas: params!.ultimasErradas ? 1 : undefined,
        },
      }),
    // Cada card é sorteado no servidor; guardar a resposta em cache faria a
    // mesma pergunta reaparecer ao voltar à página.
    gcTime: 0,
    staleTime: 0,
    ...semRepetirErroDeCliente,
  });
}

export function useAnswerCard(): UseMutationResult<
  AnswerResult,
  Error,
  { cardId: number; resposta: ReviewGrade }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<AnswerResult>('/estudo/answer', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.decks });
      void qc.invalidateQueries({ queryKey: qk.desempenho });
      void qc.invalidateQueries({ queryKey: ['study'] });
    },
  });
}

export function useCard(cardId: number | null): UseQueryResult<StudyCard> {
  return useQuery({
    queryKey: qk.card(cardId ?? 0),
    enabled: cardId !== null,
    queryFn: async () =>
      (await api<{ card: StudyCard }>(`/estudo/card/${cardId}`)).card,
    ...semRepetirErroDeCliente,
  });
}

export async function iniciarSimulado(input: {
  deckId: number;
  subtopic: string | null;
  limite: number;
}): Promise<SimuladoPool> {
  return api<SimuladoPool>('/estudo/simulado/start', {
    query: {
      deckId: input.deckId,
      subtopic: input.subtopic,
      limite: input.limite,
    },
  });
}

export async function carregarCard(cardId: number): Promise<StudyCard> {
  return (await api<{ card: StudyCard }>(`/estudo/card/${cardId}`)).card;
}

export async function responderSimulado(input: {
  sessionId: number;
  cardId: number;
  acerto: boolean;
}): Promise<{ ok: true }> {
  return api<{ ok: true }>('/estudo/simulado/answer', {
    method: 'POST',
    body: input,
  });
}

export async function finalizarSimulado(input: {
  sessionId: number;
  deckId: number;
  subtopic: string | null;
}): Promise<SimuladoResult> {
  return api<SimuladoResult>('/estudo/simulado/finish', {
    method: 'POST',
    body: {
      sessionId: input.sessionId,
      deckId: input.deckId,
      ...(input.subtopic ? { subtopic: input.subtopic } : {}),
    },
  });
}

export function useFinalizarSimulado(): UseMutationResult<
  SimuladoResult,
  Error,
  { sessionId: number; deckId: number; subtopic: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: finalizarSimulado,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.desempenho });
      void qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

// ── Conteúdo ────────────────────────────────────────────────────────────────

export function useToggleFavorita(): UseMutationResult<
  { favorito: boolean },
  Error,
  { cardId: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<{ favorito: boolean }>('/favoritas/toggle', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['favoritas'] });
      void qc.invalidateQueries({ queryKey: qk.decks });
    },
  });
}

export function useSalvarNota(): UseMutationResult<
  { removida: boolean },
  Error,
  { cardId: number; texto: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<{ removida: boolean }>('/notas', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notas'] });
      void qc.invalidateQueries({ queryKey: qk.caderno });
    },
  });
}

export interface FavoriteFilters {
  page: number;
  deckId: number | null;
  sort: FavoriteSort;
  pacote: string | null;
  subtopic: string | null;
  desde: string | null;
  ate: string | null;
  ultimasErradas: boolean;
}

export function useFavoritas(
  filters: FavoriteFilters,
): UseQueryResult<FavoriteListResponse> {
  return useQuery({
    queryKey: qk.favoritas(filters),
    queryFn: () =>
      api<FavoriteListResponse>('/favoritas', {
        query: {
          page: filters.page,
          perPage: 25,
          deckId: filters.deckId,
          sort: filters.sort !== 'recent' ? filters.sort : undefined,
          pacote: filters.pacote,
          subtopic: filters.subtopic,
          desde: filters.desde,
          ate: filters.ate,
          ultimasErradas: filters.ultimasErradas ? 1 : undefined,
        },
      }),
    ...semRepetirErroDeCliente,
  });
}

export function useNotas(page: number): UseQueryResult<Paged<NoteItem>> {
  return useQuery({
    queryKey: qk.notas(page),
    queryFn: () => api<Paged<NoteItem>>('/notas', { query: { page, perPage: 25 } }),
    ...semRepetirErroDeCliente,
  });
}

export function useComentarios(cardId: number | null): UseQueryResult<CommentView[]> {
  return useQuery({
    queryKey: qk.comentarios(cardId ?? 0),
    enabled: cardId !== null,
    queryFn: async () =>
      (await api<{ comentarios: CommentView[] }>(`/comentarios/${cardId}`)).comentarios,
    ...semRepetirErroDeCliente,
  });
}

export function usePostarComentario(
  cardId: number,
): UseMutationResult<{ id: number }, Error, { texto: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<{ id: number }>('/comentarios', {
        method: 'POST',
        body: { cardId, texto: input.texto },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.comentarios(cardId) }),
  });
}

export function useVotarComentario(
  cardId: number,
): UseMutationResult<
  { saldo: number; meuVoto: number },
  Error,
  { commentId: number; valor: 1 | -1 }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<{ saldo: number; meuVoto: number }>('/comentarios/votar', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.comentarios(cardId) }),
  });
}

export function useApagarComentario(
  cardId: number,
): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api<void>(`/comentarios/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.comentarios(cardId) }),
  });
}

export function useReportar(): UseMutationResult<
  { jaReportado: boolean },
  Error,
  { cardId: number }
> {
  return useMutation({
    mutationFn: (input) =>
      api<{ jaReportado: boolean }>('/reportar', { method: 'POST', body: input }),
  });
}

export function useCaderno(): UseQueryResult<{
  total: number;
  card: StudyCard | null;
  texto: string | null;
}> {
  return useQuery({
    queryKey: qk.caderno,
    queryFn: () =>
      api<{ total: number; card: StudyCard | null; texto: string | null }>('/caderno'),
    gcTime: 0,
    staleTime: 0,
    ...semRepetirErroDeCliente,
  });
}

// ── Estatísticas ────────────────────────────────────────────────────────────

export function useStats(
  desde: string,
  ate: string,
  deckId: number | null,
): UseQueryResult<StatsOverview> {
  return useQuery({
    queryKey: qk.stats(desde, ate, deckId),
    queryFn: () =>
      api<StatsOverview>('/estatisticas', { query: { desde, ate, deckId } }),
    ...semRepetirErroDeCliente,
  });
}

export function useDesempenho(): UseQueryResult<PerformanceDashboard> {
  return useQuery({
    queryKey: qk.desempenho,
    queryFn: () => api<PerformanceDashboard>('/planner/desempenho'),
    staleTime: 0,
    refetchOnMount: 'always',
    ...semRepetirErroDeCliente,
  });
}

export function useTempoHoje(): UseQueryResult<{
  focusSeconds: number;
  focusMinutes: number;
}> {
  return useQuery({
    queryKey: qk.tempoHoje,
    queryFn: () =>
      api<{ focusSeconds: number; focusMinutes: number }>('/estatisticas/tempo/hoje'),
    staleTime: 30_000,
    ...semRepetirErroDeCliente,
  });
}

export function useZerarProgresso(): UseMutationResult<
  { reviews: number; logs: number },
  Error,
  { deckId: number | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<{ reviews: number; logs: number }>('/estatisticas/reset', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

// ── Planeador ───────────────────────────────────────────────────────────────

export function usePlanner(de: string, ate: string): UseQueryResult<PlannerRange> {
  return useQuery({
    queryKey: qk.planner(de, ate),
    queryFn: () => api<PlannerRange>('/planner', { query: { de, ate } }),
    ...semRepetirErroDeCliente,
  });
}

export function usePlannerSubtopics(
  deckId: number | null,
): UseQueryResult<Array<{ label: string; path: string }>> {
  return useQuery({
    queryKey: qk.plannerSubtopics(deckId ?? 0),
    enabled: deckId !== null,
    queryFn: async () =>
      (
        await api<{ options: Array<{ label: string; path: string }> }>(
          '/planner/subtopics',
          { query: { deckId } },
        )
      ).options,
    staleTime: 10 * 60_000,
    ...semRepetirErroDeCliente,
  });
}

export function useSalvarPlanner(): UseMutationResult<
  { criados: number },
  Error,
  {
    entriesByDay: Record<string, Array<{ deckId: number; subtopicPath?: string }>>;
    replicaAte: string | null;
  }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<{ criados: number }>('/planner/save', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planner'] }),
  });
}

export function useConcluirTopico(): UseMutationResult<
  { ok: boolean },
  Error,
  { deckId: number; subtopicPath?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<{ ok: boolean }>('/planner/complete', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planner'] }),
  });
}

export function useLimparPlanner(): UseMutationResult<{ ok: boolean }, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: boolean }>('/planner/clear', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planner'] }),
  });
}

export function useGerarPlanoAutomatico(): UseMutationResult<
  AutoPlanResult,
  Error,
  {
    examName?: string;
    tempoMinutesPerDay?: number;
    daysCount: number;
    pacotes: string[];
  }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<AutoPlanResult>('/planner/auto', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planner'] }),
  });
}

export function useEditais(): UseQueryResult<Edict[]> {
  return useQuery({
    queryKey: qk.editais,
    queryFn: async () => (await api<{ edicts: Edict[] }>('/planner/edital')).edicts,
    ...semRepetirErroDeCliente,
  });
}

export function useSalvarEdital(): UseMutationResult<
  { id: number },
  Error,
  {
    id?: number | null;
    examName: string;
    proofDate?: string | null;
    daysRemaining?: number | null;
    tempoMinutesPerDay?: number | null;
    deckIds?: number[];
  }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<{ id: number }>('/planner/edital', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.editais }),
  });
}

export function useApagarEdital(): UseMutationResult<{ ok: boolean }, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api<{ ok: boolean }>(`/planner/edital/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.editais }),
  });
}

// ── Conta ───────────────────────────────────────────────────────────────────

export function useConta(): UseQueryResult<AccountOverview> {
  return useQuery({
    queryKey: qk.conta,
    queryFn: () => api<AccountOverview>('/conta'),
    ...semRepetirErroDeCliente,
  });
}

export function useLedger(): UseQueryResult<LedgerEntry[]> {
  return useQuery({
    queryKey: qk.ledger,
    queryFn: async () =>
      (await api<{ movimentos: LedgerEntry[] }>('/conta/ledger')).movimentos,
    ...semRepetirErroDeCliente,
  });
}

export function useSessoes(): UseQueryResult<SessionRow[]> {
  return useQuery({
    queryKey: qk.sessoes,
    queryFn: async () => (await api<{ sessoes: SessionRow[] }>('/conta/sessions')).sessoes,
    ...semRepetirErroDeCliente,
  });
}

export async function cotarTrocaDePlano(planCode: string): Promise<ChangePlanQuote> {
  return api<ChangePlanQuote>('/conta/quote', { query: { planCode } });
}

export function useTrocarPlano(): UseMutationResult<
  {
    aplicado: boolean;
    precisaPagar: boolean;
    quote: ChangePlanQuote;
    mensagem?: string;
  },
  Error,
  { planCode: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<{
        aplicado: boolean;
        precisaPagar: boolean;
        quote: ChangePlanQuote;
        mensagem?: string;
      }>('/conta/change-plan', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.conta });
      void qc.invalidateQueries({ queryKey: qk.ledger });
    },
  });
}

export function useCancelarAssinatura(): UseMutationResult<
  CancelOutcome,
  Error,
  { motivo?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<CancelOutcome>('/conta/cancel', {
        method: 'POST',
        body: { ...input, confirmar: true },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.conta });
      await qc.invalidateQueries({ queryKey: qk.ledger });
    },
  });
}

export function useEncerrarSessoes(): UseMutationResult<
  { encerradas: number },
  Error,
  void
> {
  return useMutation({
    mutationFn: () =>
      api<{ encerradas: number }>('/conta/sessions/revoke-all', { method: 'POST' }),
  });
}
